import jsPDF from 'jspdf';
import { Slide, AspectRatio } from '../types';

// 현재 DOM에서 실제 렌더링된 슬라이드 HTML 추출
const extractRenderedSlideHTML = (slideId: string): string => {
  const slideElement = document.querySelector(`[data-slide-id="${slideId}"]`);
  if (!slideElement) {return '';}
  
  const slideRenderer = slideElement.querySelector('.slide-renderer-container');
  if (!slideRenderer) {return '';}
  
  // 슬라이드 렌더러의 전체 HTML과 스타일을 추출
  const clonedElement = slideRenderer.cloneNode(true) as HTMLElement;
  
  // transform 스타일 제거 (스케일링 제거)
  clonedElement.style.transform = 'none';
  clonedElement.style.transformOrigin = 'initial';
  
  return clonedElement.outerHTML;
};

// 현재 앱에서 사용하는 모든 CSS 스타일 추출
const extractAppStyles = (): string => {
  const styleSheets = Array.from(document.styleSheets);
  let css = '';
  
  for (const styleSheet of styleSheets) {
    try {
      const rules = Array.from(styleSheet.cssRules || styleSheet.rules || []);
      for (const rule of rules) {
        css += rule.cssText + '\n';
      }
    } catch (e) {
      // Cross-origin stylesheets can't be accessed
      if (process.env.NODE_ENV === 'development') {
        console.log('Cannot access stylesheet:', e);
      }
    }
  }
  
  return css;
};

export const exportToPDF = async (slides: Slide[], aspectRatio: AspectRatio) => {
  const html2canvas = (await import('html2canvas')).default;
  // 새 창에서 PDF 생성 처리
  const newWindow = window.open('', '_blank');
  if (!newWindow) {
    alert('팝업이 차단되었습니다. 팝업을 허용해주세요.');
    return;
  }

  newWindow.document.write(`
    <html>
      <head>
        <title>PDF 생성 중...</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            display: flex;
            justify-content: center;
            align-items: center;
            height: 100vh;
            margin: 0;
            background: #f5f5f5;
          }
          .container {
            text-align: center;
            padding: 2rem;
            background: white;
            border-radius: 8px;
            box-shadow: 0 2px 10px rgba(0,0,0,0.1);
          }
          .spinner {
            border: 4px solid #f3f3f3;
            border-top: 4px solid #3498db;
            border-radius: 50%;
            width: 40px;
            height: 40px;
            animation: spin 1s linear infinite;
            margin: 0 auto 1rem;
          }
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="spinner"></div>
          <h2>PDF 생성 중...</h2>
          <p>잠시만 기다려주세요.</p>
        </div>
      </body>
    </html>
  `);

  try {
    // PDF 설정
    const pdfWidth = 297;
    const pdfHeight = (pdfWidth * aspectRatio.height) / aspectRatio.width;
    
    const pdf = new jsPDF({
      orientation: aspectRatio.width > aspectRatio.height ? 'landscape' : 'portrait',
      unit: 'mm',
      format: [pdfWidth, pdfHeight],
    });

    for (let i = 0; i < slides.length; i++) {
      if (i > 0) {
        pdf.addPage();
      }

      // 진행률 업데이트
      newWindow.document.body.innerHTML = `
        <div class="container">
          <div class="spinner"></div>
          <h2>PDF 생성 중...</h2>
          <p>슬라이드 ${i + 1}/${slides.length} 처리 중</p>
          <div style="width: 100%; background: #ddd; border-radius: 10px; margin-top: 1rem;">
            <div style="width: ${((i + 1) / slides.length) * 100}%; height: 20px; background: #3498db; border-radius: 10px; transition: width 0.3s;"></div>
          </div>
        </div>
      `;

      try {
        // 기존 로직과 동일
        const slideElement = document.querySelector(`[data-slide-id="${slides[i].id}"]`) as HTMLElement;
        
        if (slideElement) {
          // 슬라이드 컨테이너 내부의 실제 SlideRenderer를 찾기
          const slideRenderer = slideElement.querySelector('.slide-renderer-container') as HTMLElement;
          const targetElement = slideRenderer || slideElement;
          
          const canvas = await html2canvas(targetElement, {
            width: targetElement.offsetWidth,
            height: targetElement.offsetHeight,
            scale: 2, // 스케일 줄여서 속도 향상
            backgroundColor: '#ffffff',
            useCORS: true,
            allowTaint: true,
            logging: false,
            onclone: (clonedDoc) => {
              // 클론된 문서에서 스타일 보정
              const clonedElement = clonedDoc.querySelector(`[data-slide-id="${slides[i].id}"] .slide-renderer-container`) || 
                                  clonedDoc.querySelector(`[data-slide-id="${slides[i].id}"]`);
              if (clonedElement) {
                (clonedElement as HTMLElement).style.transform = 'none';
                (clonedElement as HTMLElement).style.transformOrigin = 'initial';
              }
            },
            imageTimeout: 10000,
          });

          const imgData = canvas.toDataURL('image/jpeg', 0.8);
          pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight);
        } else {
          // 폴백 처리
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = slides[i].htmlContent;
          tempDiv.style.position = 'absolute';
          tempDiv.style.left = '-9999px';
          tempDiv.style.width = '1000px';
          tempDiv.style.height = `${(1000 * aspectRatio.height) / aspectRatio.width}px`;
          tempDiv.style.backgroundColor = '#ffffff';
          document.body.appendChild(tempDiv);

          const canvas = await html2canvas(tempDiv, {
            width: 1000,
            height: (1000 * aspectRatio.height) / aspectRatio.width,
            scale: 1.5,
            backgroundColor: '#ffffff',
            useCORS: true,
            allowTaint: true,
            logging: false,
          });

          document.body.removeChild(tempDiv);
          const imgData = canvas.toDataURL('image/jpeg', 0.8);
          pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight);
        }
      } catch {  // Removed unused error variable
        // console.error('Failed to render slide to PDF:', slideError);
        // Fallback to text-only rendering when HTML rendering fails
        pdf.setFontSize(24);
        pdf.text(slides[i].title, 20, 30);
        pdf.setFontSize(14);
        const contentLines = pdf.splitTextToSize(slides[i].content, pdfWidth - 40);
        pdf.text(contentLines, 20, 50);
      }
    }

    // 완료 후 다운로드
    newWindow.document.body.innerHTML = `
      <div class="container">
        <h2>✅ PDF 생성 완료!</h2>
        <p>다운로드가 시작됩니다...</p>
        <button onclick="window.close()" style="margin-top: 1rem; padding: 0.5rem 1rem; background: #3498db; color: white; border: none; border-radius: 4px; cursor: pointer;">창 닫기</button>
      </div>
    `;

    pdf.save('presentation.pdf');
    
    // 3초 후 창 자동 닫기
    setTimeout(() => {
      newWindow.close();
    }, 3000);

  } catch {  // Removed unused error variable
    // console.error('PDF export failed:', error);
    // Show error message to user in the popup window
    newWindow.document.body.innerHTML = `
      <div class="container">
        <h2>❌ 오류 발생</h2>
        <p>PDF 생성 중 오류가 발생했습니다.</p>
        <button onclick="window.close()" style="margin-top: 1rem; padding: 0.5rem 1rem; background: #e74c3c; color: white; border: none; border-radius: 4px; cursor: pointer;">창 닫기</button>
      </div>
    `;
  }
};

export const exportToHTML = (slides: Slide[], aspectRatio: AspectRatio, presentationMode?: boolean) => {
  if (presentationMode) {
    exportToHTMLPresentation(slides, aspectRatio);
  } else {
    // 사용자에게 이미지 포함 옵션 제공
    const includeImages = window.confirm(
      '이미지를 HTML 파일에 포함시키겠습니까?\n\n' +
      '✅ 예: HTML 파일에 이미지가 BASE64로 포함됩니다 (파일 크기 증가)\n' +
      '❌ 아니오: HTML 파일만 내보냅니다 (이미지는 별도 링크)'
    );

    if (includeImages) {
      exportToHTMLWithImages(slides, aspectRatio);
    } else {
      exportToHTMLBasic(slides, aspectRatio);
    }
  }
};

const exportToHTMLPresentation = (slides: Slide[], aspectRatio: AspectRatio) => {
  // 현재 화면에서 실제 렌더링된 슬라이드들 추출
  const renderedSlides = slides.map(slide => ({
    id: slide.id,
    title: slide.title,
    order: slide.order,
    html: extractRenderedSlideHTML(slide.id)
  })).filter(slide => slide.html); // HTML이 있는 것만 포함
  
  if (renderedSlides.length === 0) {
    alert('내보낼 슬라이드가 없습니다. 슬라이드를 먼저 생성해주세요.');
    return;
  }

  // 현재 앱 스타일 추출
  const appStyles = extractAppStyles();
  
  const htmlContent = `
<!DOCTYPE html>
<html lang="ko">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>AI 프레젠테이션</title>
    <style>
        /* 앱에서 추출한 스타일 */
        ${appStyles}
        
        /* 프레젠테이션 전용 스타일 */
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }
        
        body {
            background: #000;
            overflow: hidden;
            font-family: 'Segoe UI', system-ui, -apple-system, sans-serif;
            cursor: none;
        }
        
        .presentation-container {
            width: 100vw;
            height: 100vh;
            position: relative;
            display: flex;
            align-items: center;
            justify-content: center;
        }
        
        .slide {
            position: absolute;
            width: 100%;
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            opacity: 0;
            transform: translateX(100%);
            transition: all 0.6s cubic-bezier(0.4, 0.0, 0.2, 1);
        }
        
        .slide.active {
            opacity: 1;
            transform: translateX(0);
        }
        
        .slide.prev {
            transform: translateX(-100%);
        }
        
        .slide-content {
            width: ${aspectRatio.width}px;
            height: ${aspectRatio.height}px;
            max-width: 100vw;
            max-height: 100vh;
            transform-origin: center;
            position: relative;
        }
        
        /* 슬라이드가 화면에 맞게 스케일링 */
        @media screen {
            .slide-content {
                transform: scale(min(100vw/${aspectRatio.width}, 100vh/${aspectRatio.height}));
            }
        }
        
        .slide-renderer-container {
            width: 100% !important;
            height: 100% !important;
            transform: none !important;
        }
        
        .progress-bar {
            position: fixed;
            bottom: 0;
            left: 0;
            width: 100%;
            height: 3px;
            background: rgba(255, 255, 255, 0.1);
            z-index: 1000;
        }
        
        .progress-fill {
            height: 100%;
            background: #3B82F6;
            transition: width 0.6s ease;
        }
        
        .controls {
            position: fixed;
            bottom: 20px;
            right: 20px;
            color: white;
            font-size: 14px;
            opacity: 0.7;
            z-index: 1001;
        }
    </style>
</head>
<body>
    <div class="presentation-container">
        ${renderedSlides.map((slide, index) => `
            <div class="slide ${index === 0 ? 'active' : ''}" data-slide="${index}">
                <div class="slide-content">
                    ${slide.html}
                </div>
            </div>
        `).join('')}
        
        <div class="progress-bar">
            <div class="progress-fill" style="width: ${100 / renderedSlides.length}%"></div>
        </div>
        
        <div class="controls">
            <div>슬라이드 <span id="current-slide">1</span> / ${renderedSlides.length}</div>
            <div style="margin-top: 5px; font-size: 12px;">← → 화살표 키 또는 클릭으로 이동</div>
        </div>
    </div>

    <script>
        let currentSlide = 0;
        const totalSlides = ${renderedSlides.length};
        
        function updateSlide() {
            document.querySelectorAll('.slide').forEach((slide, index) => {
                slide.classList.remove('active', 'prev');
                if (index === currentSlide) {
                    slide.classList.add('active');
                } else if (index < currentSlide) {
                    slide.classList.add('prev');
                }
            });
            
            const progress = ((currentSlide + 1) / totalSlides) * 100;
            document.querySelector('.progress-fill').style.width = progress + '%';
            document.getElementById('current-slide').textContent = currentSlide + 1;
        }
        
        function nextSlide() {
            if (currentSlide < totalSlides - 1) {
                currentSlide++;
                updateSlide();
            }
        }
        
        function prevSlide() {
            if (currentSlide > 0) {
                currentSlide--;
                updateSlide();
            }
        }
        
        // 키보드 이벤트
        document.addEventListener('keydown', (e) => {
            switch(e.key) {
                case 'ArrowRight':
                case ' ':
                    e.preventDefault();
                    nextSlide();
                    break;
                case 'ArrowLeft':
                    e.preventDefault();
                    prevSlide();
                    break;
                case 'Home':
                    e.preventDefault();
                    currentSlide = 0;
                    updateSlide();
                    break;
                case 'End':
                    e.preventDefault();
                    currentSlide = totalSlides - 1;
                    updateSlide();
                    break;
                case 'Escape':
                    if (document.fullscreenElement) {
                        document.exitFullscreen();
                    }
                    break;
                case 'f':
                case 'F':
                    if (!document.fullscreenElement) {
                        document.documentElement.requestFullscreen();
                    }
                    break;
            }
        });
        
        // 마우스 클릭 이벤트
        document.addEventListener('click', nextSlide);
        
        // 페이지 로드 시 전체화면 모드 진입
        document.addEventListener('DOMContentLoaded', () => {
            if (document.documentElement.requestFullscreen) {
                document.documentElement.requestFullscreen().catch(() => {
                    // 전체화면 실패 시 무시
                });
            }
        });
    </script>
</body>
</html>
  `;

  const blob = new Blob([htmlContent], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'presentation.html';
  link.click();
  URL.revokeObjectURL(url);
};

const exportToHTMLPresentationOriginal = (slides: Slide[], aspectRatio: AspectRatio) => {
  const htmlContent = `
<!DOCTYPE html>
<html lang="ko">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>AI 프레젠테이션</title>
    <style>
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }
        
        body {
            background: #000;
            overflow: hidden;
            font-family: 'Segoe UI', system-ui, -apple-system, sans-serif;
            cursor: none;
        }
        
        .presentation-container {
            width: 100vw;
            height: 100vh;
            position: relative;
            display: flex;
            align-items: center;
            justify-content: center;
        }
        
        .slide {
            position: absolute;
            width: 100%;
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            opacity: 0;
            transform: translateX(100%);
            transition: all 0.6s cubic-bezier(0.4, 0.0, 0.2, 1);
        }
        
        .slide.active {
            opacity: 1;
            transform: translateX(0);
        }
        
        .slide.prev {
            transform: translateX(-100%);
        }
        
        .slide-content {
            width: ${aspectRatio.width}px;
            height: ${aspectRatio.height}px;
            max-width: 100vw;
            max-height: 100vh;
            transform-origin: center;
            position: relative;
        }
        
        /* 슬라이드 내부의 모든 요소가 컨테이너에 맞게 스케일링되도록 */
        .slide-content > * {
            transform-origin: top left;
        }
        
        .slide-end {
            position: absolute;
            width: 100%;
            height: 100%;
            background: #000;
            color: #fff;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 2.5rem;
            font-weight: 300;
            opacity: 0;
            transition: opacity 0.6s ease;
            pointer-events: none;
        }
        
        .slide-end.show {
            opacity: 1;
            pointer-events: all;
        }
        
        .progress-bar {
            position: fixed;
            bottom: 0;
            left: 0;
            width: 100%;
            height: 3px;
            background: rgba(255, 255, 255, 0.1);
            z-index: 1000;
        }
        
        .progress-fill {
            height: 100%;
            background: rgba(255, 255, 255, 0.8);
            transition: width 0.3s ease;
        }
        
        /* 페이지 번호 표시 */
        .page-indicator {
            position: fixed;
            bottom: 20px;
            right: 20px;
            color: rgba(255, 255, 255, 0.8);
            font-size: 14px;
            background: rgba(0, 0, 0, 0.5);
            padding: 5px 15px;
            border-radius: 20px;
            z-index: 1000;
            font-weight: 300;
        }
        
        /* 도움말 표시 */
        .help-text {
            position: fixed;
            top: 20px;
            left: 50%;
            transform: translateX(-50%);
            color: rgba(255, 255, 255, 0.9);
            font-size: 14px;
            background: rgba(0, 0, 0, 0.7);
            padding: 10px 20px;
            border-radius: 8px;
            opacity: 1;
            transition: opacity 0.5s ease;
            z-index: 1000;
        }
        
        .help-text.hide {
            opacity: 0;
            pointer-events: none;
        }
        
        /* 커서 */
        .custom-cursor {
            position: fixed;
            width: 20px;
            height: 20px;
            border: 2px solid rgba(255, 255, 255, 0.5);
            border-radius: 50%;
            pointer-events: none;
            z-index: 9999;
            transition: transform 0.1s ease;
            transform: translate(-50%, -50%);
        }
        
        .custom-cursor.click {
            transform: translate(-50%, -50%) scale(0.8);
            background: rgba(255, 255, 255, 0.2);
        }
        
        /* 전체화면 메시지 */
        .fullscreen-prompt {
            position: fixed;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            background: rgba(0, 0, 0, 0.9);
            color: white;
            padding: 30px 50px;
            border-radius: 10px;
            text-align: center;
            z-index: 10000;
            transition: opacity 0.3s ease;
        }
        
        .fullscreen-prompt h2 {
            margin-bottom: 20px;
            font-weight: 300;
        }
        
        .fullscreen-prompt button {
            background: white;
            color: black;
            border: none;
            padding: 10px 30px;
            border-radius: 5px;
            font-size: 16px;
            cursor: pointer;
            transition: transform 0.1s ease;
        }
        
        .fullscreen-prompt button:hover {
            transform: scale(1.05);
        }
        
        .fullscreen-prompt button:active {
            transform: scale(0.95);
        }
        
        .fullscreen-prompt.hide {
            opacity: 0;
            pointer-events: none;
        }
    </style>
</head>
<body>
    <div class="custom-cursor" id="customCursor"></div>
    
    <div class="fullscreen-prompt" id="fullscreenPrompt">
        <h2>프레젠테이션을 시작하려면 클릭하세요</h2>
        <button onclick="startPresentation()">전체화면으로 시작</button>
    </div>
    
    <div class="presentation-container">
        ${slides.map((slide, index) => `
        <div class="slide ${index === 0 ? 'active' : ''}" data-slide="${index}">
            <div class="slide-content">
                ${slide.htmlContent}
            </div>
        </div>
        `).join('')}
        
        <div class="slide-end" id="slideEnd">
            <div>슬라이드가 종료되었습니다.</div>
        </div>
    </div>
    
    <div class="progress-bar">
        <div class="progress-fill" id="progressFill" style="width: ${100 / slides.length}%"></div>
    </div>
    
    <div class="page-indicator" id="pageIndicator">
        1 / ${slides.length}
    </div>
    
    <div class="help-text" id="helpText">
        스페이스바 또는 클릭: 다음 | 백스페이스: 이전 | ESC: 전체화면 종료
    </div>
    
    <script>
        let currentSlide = 0;
        const totalSlides = ${slides.length};
        const slides = document.querySelectorAll('.slide');
        const slideEnd = document.getElementById('slideEnd');
        const progressFill = document.getElementById('progressFill');
        const pageIndicator = document.getElementById('pageIndicator');
        const helpText = document.getElementById('helpText');
        const customCursor = document.getElementById('customCursor');
        const fullscreenPrompt = document.getElementById('fullscreenPrompt');
        let isEnded = false;
        let presentationStarted = false;
        
        // 커스텀 커서
        document.addEventListener('mousemove', (e) => {
            customCursor.style.left = e.clientX + 'px';
            customCursor.style.top = e.clientY + 'px';
        });
        
        document.addEventListener('mousedown', () => {
            customCursor.classList.add('click');
        });
        
        document.addEventListener('mouseup', () => {
            customCursor.classList.remove('click');
        });
        
        // 전체화면 시작
        function enterFullscreen() {
            const elem = document.documentElement;
            if (elem.requestFullscreen) {
                elem.requestFullscreen();
            } else if (elem.webkitRequestFullscreen) {
                elem.webkitRequestFullscreen();
            } else if (elem.msRequestFullscreen) {
                elem.msRequestFullscreen();
            } else if (elem.mozRequestFullScreen) {
                elem.mozRequestFullScreen();
            }
        }
        
        // 프레젠테이션 시작
        function startPresentation() {
            enterFullscreen();
            fullscreenPrompt.classList.add('hide');
            presentationStarted = true;
            document.body.style.cursor = 'none';
            resizeSlides();
            
            // 도움말 3초 후 숨기기
            setTimeout(() => {
                helpText.classList.add('hide');
            }, 3000);
        }
        
        // 슬라이드 크기 조정
        function resizeSlides() {
            const slideContents = document.querySelectorAll('.slide-content');
            const windowRatio = window.innerWidth / window.innerHeight;
            const slideRatio = ${aspectRatio.width} / ${aspectRatio.height};
            
            slideContents.forEach(slideContent => {
                let scale;
                if (windowRatio > slideRatio) {
                    scale = window.innerHeight / ${aspectRatio.height};
                } else {
                    scale = window.innerWidth / ${aspectRatio.width};
                }
                slideContent.style.transform = \`scale(\${scale})\`;
            });
        }
        
        // 슬라이드 전환
        function goToSlide(index) {
            if (index < 0 || index > totalSlides) return;
            
            // 모든 슬라이드 상태 초기화
            slides.forEach((slide, i) => {
                slide.classList.remove('active', 'prev');
                if (i < index) {
                    slide.classList.add('prev');
                }
            });
            
            // 종료 화면 처리
            if (index === totalSlides) {
                slideEnd.classList.add('show');
                isEnded = true;
                progressFill.style.width = '100%';
                pageIndicator.style.display = 'none';
            } else {
                slideEnd.classList.remove('show');
                isEnded = false;
                slides[index].classList.add('active');
                progressFill.style.width = \`\${((index + 1) / totalSlides) * 100}%\`;
                pageIndicator.style.display = 'block';
                pageIndicator.textContent = \`\${index + 1} / \${totalSlides}\`;
            }
            
            currentSlide = index;
        }
        
        // 다음 슬라이드
        function nextSlide() {
            if (!presentationStarted) return;
            if (currentSlide < totalSlides) {
                goToSlide(currentSlide + 1);
            }
        }
        
        // 이전 슬라이드
        function prevSlide() {
            if (!presentationStarted) return;
            if (currentSlide > 0) {
                goToSlide(currentSlide - 1);
            }
        }
        
        // 이벤트 리스너
        document.addEventListener('click', (e) => {
            if (e.target.closest('.fullscreen-prompt')) return;
            nextSlide();
        });
        
        document.addEventListener('keydown', (e) => {
            switch(e.key) {
                case ' ':
                case 'ArrowRight':
                    e.preventDefault();
                    nextSlide();
                    break;
                case 'Backspace':
                case 'ArrowLeft':
                    e.preventDefault();
                    prevSlide();
                    break;
                case 'Escape':
                    if (document.fullscreenElement) {
                        document.exitFullscreen();
                    }
                    break;
                case 'f':
                case 'F':
                    if (!document.fullscreenElement) {
                        startPresentation();
                    }
                    break;
            }
        });
        
        // 리사이즈 처리
        window.addEventListener('resize', resizeSlides);
        
        // 전체화면 변경 감지
        document.addEventListener('fullscreenchange', () => {
            if (!document.fullscreenElement) {
                document.body.style.cursor = 'auto';
                customCursor.style.display = 'none';
            } else {
                document.body.style.cursor = 'none';
                customCursor.style.display = 'block';
            }
        });
        
        // 초기화
        window.addEventListener('load', () => {
            // 자동으로 전체화면 시작 시도
            const autoStart = () => {
                if (document.readyState === 'complete') {
                    // 약간의 지연 후 자동 시작 시도
                    setTimeout(() => {
                        // 사용자 제스처 없이는 전체화면을 시작할 수 없으므로
                        // 프롬프트를 표시
                        fullscreenPrompt.style.display = 'block';
                    }, 100);
                }
            };
            
            if (document.readyState === 'complete') {
                autoStart();
            } else {
                window.addEventListener('load', autoStart);
            }
        });
    </script>
</body>
</html>
  `;

  downloadHTMLFile(htmlContent, 'ai-presentation-fullscreen.html');
};

const exportToHTMLBasic = (slides: Slide[], aspectRatio: AspectRatio) => {
  // 현재 화면에서 실제 렌더링된 슬라이드들 추출
  const renderedSlides = slides.map(slide => ({
    id: slide.id,
    title: slide.title,
    order: slide.order,
    html: extractRenderedSlideHTML(slide.id)
  })).filter(slide => slide.html); // HTML이 있는 것만 포함
  
  if (renderedSlides.length === 0) {
    alert('내보낼 슬라이드가 없습니다. 슬라이드를 먼저 생성해주세요.');
    return;
  }

  // 현재 앱 스타일 추출
  const appStyles = extractAppStyles();
  
  const htmlContent = `
<!DOCTYPE html>
<html lang="ko">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>AI 생성 프레젠테이션</title>
    <style>
        /* 앱에서 추출한 스타일 */
        ${appStyles}
        
        /* 기본 HTML 내보내기 전용 스타일 */
        body {
            margin: 0;
            padding: 20px;
            font-family: 'Segoe UI', system-ui, -apple-system, sans-serif;
            background: #f5f5f5;
            line-height: 1.6;
        }
        .slide {
            margin: 2rem auto;
            max-width: ${aspectRatio.width + 40}px;
            box-shadow: 0 8px 32px rgba(0,0,0,0.1);
            border-radius: 16px;
            overflow: hidden;
            background: white;
            padding: 20px;
        }
        .slide-content {
            width: ${aspectRatio.width}px;
            height: ${aspectRatio.height}px;
            margin: 0 auto;
        }
        .slide-renderer-container {
            width: 100% !important;
            height: 100% !important;
            transform: none !important;
        }
        .slide-title {
            text-align: center;
            color: #333;
            margin-bottom: 1rem;
            font-size: 1.2em;
            font-weight: 600;
        }
        @media print {
            body { background: white; padding: 0; }
            .slide {
                page-break-after: always;
                margin: 0;
                max-width: none;
                box-shadow: none;
                border-radius: 0;
                padding: 10px;
            }
        }
        @media (max-width: 768px) {
            body { padding: 10px; }
            .slide { margin: 1rem auto; }
            .slide-content {
                transform: scale(min(100vw/${aspectRatio.width}, 1));
                transform-origin: top center;
            }
        }
    </style>
</head>
<body>
    <h1 style="text-align: center; color: #333; margin-bottom: 2rem;">AI 생성 프레젠테이션</h1>
    ${renderedSlides.map((slide, index) => `
        <div class="slide">
            <div class="slide-title">슬라이드 ${index + 1}${slide.title ? ` - ${slide.title}` : ''}</div>
            <div class="slide-content">
                ${slide.html}
            </div>
        </div>
    `).join('')}
    
    <div style="text-align: center; margin-top: 2rem; color: #666; font-size: 0.9rem;">
        <p>총 ${slides.length}개 슬라이드 | AI 슬라이드 생성기로 제작</p>
    </div>
</body>
</html>
  `;

  downloadHTMLFile(htmlContent, 'ai-presentation.html');
};

const exportToHTMLWithImages = async (slides: Slide[], aspectRatio: AspectRatio) => {
  try {
    // 현재 화면에서 실제 렌더링된 슬라이드들 추출
    const renderedSlides = slides.map(slide => ({
      id: slide.id,
      title: slide.title,
      order: slide.order,
      html: extractRenderedSlideHTML(slide.id),
      originalSlide: slide
    })).filter(slide => slide.html); // HTML이 있는 것만 포함
    
    if (renderedSlides.length === 0) {
      alert('내보낼 슬라이드가 없습니다. 슬라이드를 먼저 생성해주세요.');
      return;
    }

    // 모든 이미지를 BASE64로 변환
    const processedSlides = await Promise.all(renderedSlides.map(async (slideData) => {
      let processedContent = slideData.html;
      const slide = slideData.originalSlide;
      
      // 배경 이미지 처리
      if (slide.backgroundImage && !slide.backgroundImage.startsWith('data:')) {
        try {
          const base64Image = await imageToBase64(slide.backgroundImage);
          processedContent = processedContent.replace(
            new RegExp(slide.backgroundImage.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'),
            base64Image
          );
        } catch {  // Removed unused error variable
          // console.error('Failed to convert background image to base64:', error);
          // Continue with original URL if conversion fails
        }
      }
      
      // 요소 내 이미지 및 비디오 처리
      if (slide.elements) {
        for (const element of slide.elements) {
          if ((element.type === 'image' || element.type === 'video') && element.content && !element.content.startsWith('data:')) {
            try {
              const base64Data = element.type === 'image' 
                ? await imageToBase64(element.content)
                : await videoToBase64(element.content);
              processedContent = processedContent.replace(
                new RegExp(element.content.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'),
                base64Data
              );
            } catch {  // Removed unused error variable
              // console.error('Failed to convert element media to base64:', error);
              // Continue with original URL if conversion fails
            }
          }
        }
      }
      
      return { ...slideData, html: processedContent };
    }));

    // 현재 앱 스타일 추출
    const appStyles = extractAppStyles();
    
    const htmlContent = `
<!DOCTYPE html>
<html lang="ko">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>AI 생성 프레젠테이션 (이미지 포함)</title>
    <style>
        /* 앱에서 추출한 스타일 */
        ${appStyles}
        
        /* 이미지 포함 HTML 내보내기 전용 스타일 */
        body {
            margin: 0;
            padding: 20px;
            font-family: 'Segoe UI', system-ui, -apple-system, sans-serif;
            background: #f5f5f5;
            line-height: 1.6;
        }
        .slide {
            margin: 2rem auto;
            max-width: ${aspectRatio.width + 40}px;
            box-shadow: 0 8px 32px rgba(0,0,0,0.1);
            border-radius: 16px;
            overflow: hidden;
            background: white;
            padding: 20px;
        }
        .slide-content {
            width: ${aspectRatio.width}px;
            height: ${aspectRatio.height}px;
            margin: 0 auto;
        }
        .slide-renderer-container {
            width: 100% !important;
            height: 100% !important;
            transform: none !important;
        }
        .slide-title {
            text-align: center;
            color: #333;
            margin-bottom: 1rem;
            font-size: 1.2em;
            font-weight: 600;
        }
        @media print {
            body { background: white; padding: 0; }
            .slide {
                page-break-after: always;
                margin: 0;
                max-width: none;
                box-shadow: none;
                border-radius: 0;
                padding: 10px;
            }
        }
        @media (max-width: 768px) {
            body { padding: 10px; }
            .slide { margin: 1rem auto; }
            .slide-content {
                transform: scale(min(100vw/${aspectRatio.width}, 1));
                transform-origin: top center;
            }
        }
    </style>
</head>
<body>
    <h1 style="text-align: center; color: #333; margin-bottom: 2rem;">AI 생성 프레젠테이션 (이미지 포함)</h1>
    ${processedSlides.map((slideData, index) => `
        <div class="slide">
            <div class="slide-title">슬라이드 ${index + 1}${slideData.title ? ` - ${slideData.title}` : ''}</div>
            <div class="slide-content">
                ${slideData.html}
            </div>
        </div>
    `).join('')}
    
    <div style="text-align: center; margin-top: 2rem; color: #666; font-size: 0.9rem;">
        <p>총 ${processedSlides.length}개 슬라이드 | AI 슬라이드 생성기로 제작 (이미지 포함)</p>
    </div>
</body>
</html>
    `;

    downloadHTMLFile(htmlContent, 'ai-presentation-with-images.html');
  } catch {  // Removed unused error variable
    // console.error('HTML export with images failed:', error);
    alert('이미지 포함 내보내기 중 오류가 발생했습니다. 기본 HTML로 내보냅니다.');
    exportToHTMLBasic(slides, aspectRatio);
  }
};

const imageToBase64 = (imageUrl: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      canvas.width = img.width;
      canvas.height = img.height;
      
      ctx?.drawImage(img, 0, 0);
      
      try {
        const dataURL = canvas.toDataURL('image/jpeg', 0.8);
        resolve(dataURL);
      } catch (error) {
        reject(error);
      }
    };
    
    img.onerror = () => reject(new Error('이미지 로드 실패'));
    img.src = imageUrl;
  });
};

const videoToBase64 = (videoUrl: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    // Blob URL을 가져와서 실제 Blob 객체로 변환
    fetch(videoUrl)
      .then(response => response.blob())
      .then(blob => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64data = reader.result as string;
          resolve(base64data);
        };
        reader.onerror = () => reject(new Error('비디오 변환 실패'));
        reader.readAsDataURL(blob);
      })
      .catch(error => {
        reject(new Error('비디오 로드 실패: ' + error));
      });
  });
};

const downloadHTMLFile = (content: string, filename: string) => {
  const blob = new Blob([content], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};